/**
 * 交通系IC共用管理データの正本: ユーザー PC 上の transit-pass-pool.zip
 * - ディスクにデータを永続化しない（セッション中のメモリのみ）
 * - Chrome / Edge: File System Access API で選択ファイルへ直接上書き（保存ダイアログなし）
 * - ハンドルなし: メモリ上で編集し、ユーザーが明示したときだけダウンロード（都度DL・保存ダイアログなし）
 */
import { emptySnapshot, resetTppDbSession, setTppSnapshotWrittenHook, type TppSnapshot } from './tppLocalDb';
import { tppLocalStore } from './tppLocalStore';
import {
  clearStoredTppZipHandle,
  loadStoredTppZipHandle,
  saveStoredTppZipHandle,
} from './tppZipHandleStore';
import {
  downloadTppZip,
  exportTppZip,
  importTppZip,
  parseTppZipBuffer,
} from './tppZipBundle';
import { extractLocalFilePathHint } from '../../../utils/localZip/localFilePathHint';
import {
  getLatestLocalZipRecent,
  migrateLegacyLastZipFileName,
  rememberLocalZipRecent,
} from '../../../utils/localZip/localZipRecentStore';

const ZIP_FILENAME = 'transit-pass-pool.zip';
const LAST_ZIP_NAME_KEY = 'tpp.lastZipFileName';
const SYNC_DEBOUNCE_MS = 800;

/** 再読み込み後の「開き直し」案内用（ファイル本体は保持しない） */
export function getLastTppZipFileName(): string | null {
  const recent =
    getLatestLocalZipRecent('tpp') || migrateLegacyLastZipFileName('tpp', LAST_ZIP_NAME_KEY);
  if (recent?.name) return recent.name;
  try {
    const name = localStorage.getItem(LAST_ZIP_NAME_KEY);
    return name && name.trim() ? name.trim() : null;
  } catch {
    return null;
  }
}

function rememberLastZipFileName(fileName: string, pathHint?: string | null): void {
  rememberLocalZipRecent('tpp', { name: fileName, path: pathHint ?? null });
  try {
    localStorage.setItem(LAST_ZIP_NAME_KEY, fileName);
  } catch {
    // private mode 等では無視
  }
}

const ZIP_ACCEPT = {
  types: [{ description: 'Transit Pass Pool data', accept: { 'application/zip': ['.zip'] } }],
};

type HandleWithPermission = FileSystemFileHandle & {
  queryPermission?(opts: { mode: 'readwrite' }): Promise<PermissionState>;
  requestPermission?(opts: { mode: 'readwrite' }): Promise<PermissionState>;
};

/** file-handle: 選択ファイルへ静かに上書き / manual-download: 明示DLまでメモリのみ */
export type TppZipAutoSaveMode = 'file-handle' | 'manual-download';

export type TppZipConnection = {
  fileName: string;
  autoSaveMode: TppZipAutoSaveMode;
  lastSavedAt: string | null;
  /** ハンドルなしで未ダウンロードの変更がある */
  dirty: boolean;
  /** スナップショット差し替えのたびに増える（同名再接続でも RQ invalidate） */
  dataEpoch: number;
};

type ZipFileFingerprint = {
  lastModified: number;
  size: number;
};

export type TppZipExternalChangeResult = 'unchanged' | 'reloaded' | 'no_handle' | 'error';

let activeHandle: FileSystemFileHandle | null = null;
let activeFileName: string | null = null;
let connected = false;
let autoSaveMode: TppZipAutoSaveMode = 'manual-download';
let lastSavedAt: string | null = null;
let sessionDirty = false;
let syncTimer: ReturnType<typeof setTimeout> | null = null;
let syncInFlight: Promise<void> | null = null;
let pendingSync = false;
let lastKnownFingerprint: ZipFileFingerprint | null = null;
let dataEpoch = 0;

type ConnectionListener = () => void;
const connectionListeners = new Set<ConnectionListener>();

function notifyConnectionChange(): void {
  for (const listener of connectionListeners) {
    listener();
  }
}

export function subscribeTppZipConnection(listener: ConnectionListener): () => void {
  connectionListeners.add(listener);
  return () => connectionListeners.delete(listener);
}

function isFsaaSupported(): boolean {
  return typeof window !== 'undefined' && 'showOpenFilePicker' in window;
}

async function ensureWritePermission(handle: FileSystemFileHandle): Promise<boolean> {
  const h = handle as HandleWithPermission;
  if (typeof h.queryPermission !== 'function') return true;
  let state = await h.queryPermission({ mode: 'readwrite' });
  if (state === 'granted') return true;
  if (typeof h.requestPermission !== 'function') return false;
  state = await h.requestPermission({ mode: 'readwrite' });
  return state === 'granted';
}

async function writeBlobToHandle(handle: FileSystemFileHandle, blob: Blob): Promise<void> {
  const writable = await handle.createWritable();
  await writable.write(blob);
  await writable.close();
}

function clearPendingZipSync(): void {
  if (syncTimer) {
    clearTimeout(syncTimer);
    syncTimer = null;
  }
  pendingSync = false;
}

function setConnection(
  fileName: string,
  handle: FileSystemFileHandle | null,
  mode: TppZipAutoSaveMode,
  pathHint?: string | null
): void {
  clearPendingZipSync();
  activeHandle = handle;
  activeFileName = fileName;
  connected = true;
  autoSaveMode = mode;
  sessionDirty = false;
  lastSavedAt = mode === 'file-handle' ? new Date().toISOString() : null;
  dataEpoch += 1;
  rememberLastZipFileName(fileName, pathHint);
  if (handle) {
    void saveStoredTppZipHandle(handle);
  }
  notifyConnectionChange();
}

function disconnectInternal(): void {
  clearPendingZipSync();
  activeHandle = null;
  activeFileName = null;
  connected = false;
  autoSaveMode = 'manual-download';
  lastSavedAt = null;
  sessionDirty = false;
  lastKnownFingerprint = null;
  resetTppDbSession();
  notifyConnectionChange();
}

function fingerprintOf(file: File): ZipFileFingerprint {
  return { lastModified: file.lastModified, size: file.size };
}

function fingerprintsEqual(a: ZipFileFingerprint | null, b: ZipFileFingerprint | null): boolean {
  return a != null && b != null && a.lastModified === b.lastModified && a.size === b.size;
}

async function refreshFingerprintFromHandle(): Promise<ZipFileFingerprint | null> {
  if (!activeHandle) return null;
  const file = await activeHandle.getFile();
  return fingerprintOf(file);
}

export function isTppZipConnected(): boolean {
  return connected;
}

export function getTppZipConnection(): TppZipConnection | null {
  if (!connected || !activeFileName) return null;
  return {
    fileName: activeFileName,
    autoSaveMode,
    lastSavedAt,
    dirty: sessionDirty,
    dataEpoch,
  };
}

export function isTppFsaaSupported(): boolean {
  return isFsaaSupported();
}

export type TppZipRestoreResult = 'restored' | 'needs_gesture' | 'none' | 'error';

/** 保存済みハンドルの有無（ゲートの「再開」表示用） */
export async function peekStoredTppZipResume(): Promise<{ fileName: string } | null> {
  if (!isFsaaSupported()) return null;
  const handle = await loadStoredTppZipHandle();
  if (!handle) return null;
  const name = (handle.name || getLastTppZipFileName() || ZIP_FILENAME).trim();
  return { fileName: name || ZIP_FILENAME };
}

/**
 * 権限が既に granted なら無操作で再開。
 * prompt / denied のときは needs_gesture（ユーザー操作で resumeLastTppZip）。
 */
export async function tryRestoreTppZipSession(): Promise<TppZipRestoreResult> {
  if (connected) return 'restored';
  if (!isFsaaSupported()) return 'none';
  const handle = await loadStoredTppZipHandle();
  if (!handle) return 'none';

  const h = handle as HandleWithPermission;
  try {
    const state =
      typeof h.queryPermission === 'function'
        ? await h.queryPermission({ mode: 'readwrite' })
        : 'granted';
    if (state !== 'granted') return 'needs_gesture';
    await connectWithHandle(handle);
    return 'restored';
  } catch {
    await clearStoredTppZipHandle();
    return 'error';
  }
}

/** 前回 Zip をワンクリック再開（ユーザー操作内で permission を要求） */
export async function resumeLastTppZip(): Promise<void> {
  const handle = await loadStoredTppZipHandle();
  if (!handle) throw new Error('zip_no_stored_handle');
  try {
    await connectWithHandle(handle);
  } catch (e) {
    await clearStoredTppZipHandle();
    throw e;
  }
}

async function loadSnapshotFromHandle(handle: FileSystemFileHandle): Promise<File> {
  const file = await handle.getFile();
  const snapshot = await parseTppZipBuffer(await file.arrayBuffer());
  await tppLocalStore.replaceSnapshot(snapshot);
  lastKnownFingerprint = fingerprintOf(file);
  return file;
}

async function connectWithHandle(handle: FileSystemFileHandle, initialSnap?: TppSnapshot): Promise<void> {
  const granted = await ensureWritePermission(handle);
  let pathHint: string | null = null;
  if (initialSnap) {
    await tppLocalStore.replaceSnapshot(initialSnap);
    lastKnownFingerprint = await refreshFingerprintFromHandle();
    try {
      pathHint = extractLocalFilePathHint(await handle.getFile());
    } catch {
      pathHint = null;
    }
  } else {
    const file = await loadSnapshotFromHandle(handle);
    pathHint = extractLocalFilePathHint(file);
  }
  setConnection(handle.name, handle, granted ? 'file-handle' : 'manual-download', pathHint);
}

async function connectFromFile(file: File, handle: FileSystemFileHandle | null): Promise<void> {
  await importTppZip(file);
  lastKnownFingerprint = fingerprintOf(file);
  const pathHint = extractLocalFilePathHint(file);
  if (handle) {
    const granted = await ensureWritePermission(handle);
    setConnection(handle.name, handle, granted ? 'file-handle' : 'manual-download', pathHint);
  } else {
    setConnection(file.name, null, 'manual-download', pathHint);
  }
}

export async function checkTppZipExternalChange(): Promise<TppZipExternalChangeResult> {
  if (!connected || !activeHandle || autoSaveMode !== 'file-handle') {
    return 'no_handle';
  }
  try {
    if (syncInFlight) await syncInFlight;
    if (pendingSync) await flushTppZipSync();

    const fp = await refreshFingerprintFromHandle();
    if (fingerprintsEqual(fp, lastKnownFingerprint)) {
      return 'unchanged';
    }
    await loadSnapshotFromHandle(activeHandle);
    sessionDirty = false;
    dataEpoch += 1;
    notifyConnectionChange();
    return 'reloaded';
  } catch {
    disconnectInternal();
    return 'error';
  }
}

export async function openExistingTppZip(): Promise<void> {
  if (isFsaaSupported()) {
    try {
      const [handle] = await (
        window as unknown as Window & {
          showOpenFilePicker: (o?: unknown) => Promise<FileSystemFileHandle[]>;
        }
      ).showOpenFilePicker({ ...ZIP_ACCEPT, multiple: false });
      await connectWithHandle(handle);
      return;
    } catch (e) {
      if ((e as Error).name === 'AbortError') throw new Error('zip_pick_cancelled');
      const msg = e instanceof Error ? e.message : '';
      // confirm() 等でユーザー操作が切れると SecurityError / gesture 文言になる
      if (
        (e as Error).name === 'SecurityError' ||
        /user gesture/i.test(msg) ||
        /show a file picker/i.test(msg)
      ) {
        throw new Error('zip_pick_requires_gesture');
      }
      throw e;
    }
  }
  const file = await pickZipFileViaInput();
  if (!file) throw new Error('zip_pick_cancelled');
  await connectFromFile(file, null);
}

/**
 * 新規セッションをすぐ開始（保存先ダイアログ・自動ダウンロードなし）。
 * ディスクへ残すときは「Zip をダウンロード」を押す。
 */
export async function createNewTppZip(): Promise<void> {
  await clearStoredTppZipHandle();
  await tppLocalStore.replaceSnapshot(emptySnapshot());
  setConnection(ZIP_FILENAME, null, 'manual-download');
}

/**
 * ファイルピッカーなしで Zip 相当セッションを開始（自動テスト・ブラウザ QA 用）。
 * 保存は manual-download（明示ダウンロードまでメモリのみ）。
 */
export async function connectTppZipMemorySession(
  snapshot: TppSnapshot = emptySnapshot(),
  fileName = ZIP_FILENAME
): Promise<void> {
  await tppLocalStore.replaceSnapshot(snapshot);
  setConnection(fileName, null, 'manual-download');
}

/**
 * 別 Zip へ切り替え（ピッカー）。キャンセル時は現行セッションを維持する。
 * UI の「別の Zip を開く」はゲートへ戻す `disconnectTppZipSession` を優先。
 */
export async function switchTppZipFile(): Promise<void> {
  if (connected) {
    try {
      if (syncTimer) {
        clearTimeout(syncTimer);
        syncTimer = null;
      }
      await flushTppZipSync();
    } catch {
      // 保存失敗でも切り替え自体は続行（ユーザーが別 Zip を選ぶ意図）
    }
  }
  await openExistingTppZip();
}

/**
 * 接続を解除して Zip ゲートへ戻す。
 * 未DL の破棄確認は呼び出し側。メモリ上の未保存変更は捨てる。
 */
export function disconnectTppZipSession(): void {
  if (!connected) return;
  disconnectInternal();
}

function scheduleTppZipSync(): void {
  if (!connected) return;
  // 手動DL: 書き戻し不要。未DL バッジを即出す（debounce 待ちは誤解を招く）
  if (autoSaveMode === 'manual-download') {
    if (!sessionDirty) {
      sessionDirty = true;
      notifyConnectionChange();
    }
    return;
  }
  pendingSync = true;
  if (syncTimer) clearTimeout(syncTimer);
  syncTimer = setTimeout(() => {
    syncTimer = null;
    void flushTppZipSync();
  }, SYNC_DEBOUNCE_MS);
}

async function writeZipToFileHandle(blob: Blob): Promise<boolean> {
  if (autoSaveMode !== 'file-handle' || !activeHandle) return false;
  try {
    const ok = await ensureWritePermission(activeHandle);
    if (!ok) {
      autoSaveMode = 'manual-download';
      sessionDirty = true;
      return false;
    }
    await writeBlobToHandle(activeHandle, blob);
    sessionDirty = false;
    return true;
  } catch {
    autoSaveMode = 'manual-download';
    sessionDirty = true;
    return false;
  }
}

export async function flushTppZipSync(): Promise<void> {
  if (!connected) return;
  if (syncInFlight) {
    await syncInFlight;
    if (pendingSync) return flushTppZipSync();
    return;
  }

  pendingSync = false;
  syncInFlight = (async () => {
    if (autoSaveMode === 'file-handle' && activeHandle) {
      const blob = await exportTppZip();
      const wrote = await writeZipToFileHandle(blob);
      if (wrote) {
        lastSavedAt = new Date().toISOString();
        lastKnownFingerprint = await refreshFingerprintFromHandle();
      }
    } else {
      sessionDirty = true;
    }
    notifyConnectionChange();
  })();

  try {
    await syncInFlight;
  } finally {
    syncInFlight = null;
  }
}

/** 明示ダウンロードのみ。保存先ダイアログは出さない（既定のダウンロードフォルダへ）。 */
export async function downloadCurrentTppZip(): Promise<void> {
  if (!connected) return;
  if (syncTimer) {
    clearTimeout(syncTimer);
    syncTimer = null;
  }
  pendingSync = false;
  if (syncInFlight) await syncInFlight;
  const blob = await exportTppZip();
  downloadTppZip(blob, activeFileName ?? ZIP_FILENAME);
  sessionDirty = false;
  lastSavedAt = new Date().toISOString();
  notifyConnectionChange();
}

function pickZipFileViaInput(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.zip,application/zip';
    input.onchange = () => resolve(input.files?.[0] ?? null);
    input.click();
  });
}

function onPageHideFlush(): void {
  // ハンドルありのときだけ静かに書き戻す。manual ではダウンロードを起こさない
  if (!connected || autoSaveMode !== 'file-handle') return;
  if (!pendingSync && !syncInFlight) return;
  if (syncTimer) {
    clearTimeout(syncTimer);
    syncTimer = null;
  }
  void flushTppZipSync();
}

function onBeforeUnload(e: BeforeUnloadEvent): void {
  if (!connected || autoSaveMode !== 'manual-download' || !sessionDirty) return;
  e.preventDefault();
  e.returnValue = '';
}

function onVisibilityChange(): void {
  if (document.visibilityState === 'hidden') onPageHideFlush();
}

export function initTppZipAutoSync(): void {
  setTppSnapshotWrittenHook(() => scheduleTppZipSync());
  window.addEventListener('pagehide', onPageHideFlush);
  window.addEventListener('beforeunload', onBeforeUnload);
  document.addEventListener('visibilitychange', onVisibilityChange);
}

export function teardownTppZipAutoSync(): void {
  setTppSnapshotWrittenHook(null);
  window.removeEventListener('pagehide', onPageHideFlush);
  window.removeEventListener('beforeunload', onBeforeUnload);
  document.removeEventListener('visibilitychange', onVisibilityChange);
  if (syncTimer) clearTimeout(syncTimer);
  syncTimer = null;
  pendingSync = false;
}

if (typeof window !== 'undefined' && import.meta.env.DEV) {
  (
    window as unknown as {
      __connectTppZipMemorySession?: typeof connectTppZipMemorySession;
    }
  ).__connectTppZipMemorySession = connectTppZipMemorySession;
}
